import type { IOperationLogEntry } from '../../../src/models/Operation';
import { PRODUCT_NAME } from '../app/product';

/** "ABC Corporation › 2025", or "the top of the library". */
export function folderText(path: string): string {
  const i: number = path.lastIndexOf('/');
  return i === -1 ? 'the top of the library' : path.slice(0, i).split('/').join(' › ');
}

function nameOf(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1);
}

export interface IStep {
  /** One plain sentence: what happened to this item. */
  text: string;
  /** Why, when it wasn't done or only partly. */
  problem?: string;
  kind: 'moved' | 'renamed' | 'filed' | 'setAside' | 'created' | 'putBack' | 'removed' | 'tagged' | 'recorded' | 'viewed' | 'other';
}

/**
 * What one logged step did, in simple words: moved, renamed, set aside (moved to the
 * "Set aside" folder, never deleted), created a folder, or put back by an undo.
 */
export function describeStep(e: IOperationLogEntry): IStep {
  const problem: string | undefined = e.status === 'failed'
    ? `Not done: ${e.errorMessage ?? 'something went wrong'}`
    : e.status === 'skipped' ? `Skipped: ${e.errorMessage ?? 'nothing to do'}` : undefined;
  const what: string = e.itemKind === 'folder' ? 'the folder' : '';
  const q = (n: string): string => `${what ? `${what} ` : ''}"${n}"`;
  const moved: boolean = folderText(e.sourcePath) !== folderText(e.destinationPath);
  const renamed: boolean = !!e.originalName && !!e.newName && e.originalName !== e.newName;

  switch (e.action) {
    case 'createFolder':
      return { text: `Created the folder ${e.destinationPath.split('/').join(' › ')}`, problem, kind: 'created' };
    case 'quarantine':
      return { text: `Set aside ${q(e.originalName)}: moved from ${folderText(e.sourcePath)} to ${folderText(e.destinationPath)}. Not deleted.`, problem, kind: 'setAside' };
    case 'rename':
      return { text: `Renamed ${q(e.originalName)} to "${e.newName}" (in ${folderText(e.destinationPath)})`, problem, kind: 'renamed' };
    case 'move':
      if (renamed && moved) {
        return { text: `Renamed ${q(e.originalName)} to "${e.newName}" and moved it from ${folderText(e.sourcePath)} to ${folderText(e.destinationPath)}`, problem, kind: 'filed' };
      }
      if (renamed) {
        return { text: `Renamed ${q(e.originalName)} to "${e.newName}" (in ${folderText(e.destinationPath)})`, problem, kind: 'renamed' };
      }
      return { text: `Moved ${q(e.originalName || nameOf(e.sourcePath))} from ${folderText(e.sourcePath)} to ${folderText(e.destinationPath)}`, problem, kind: 'moved' };
    case 'record':
      return { text: e.reason || `Changed "${e.originalName}"`, problem, kind: 'recorded' };
    case 'view':
      return { text: e.reason || `Opened "${e.originalName}"`, problem, kind: 'viewed' };
    case 'undo':
      if (e.itemKind === 'record') {
        // An undo entry swaps the names: the record's label is in newName.
        return { text: `Put "${e.newName}" back as it was`, problem, kind: 'putBack' };
      }
      if (!e.newName && e.itemKind === 'folder') {
        return e.status === 'success'
          ? { text: `Removed the empty folder ${e.sourcePath.split('/').join(' › ')} that ${PRODUCT_NAME} had created (it's in the SharePoint recycle bin)`, kind: 'removed' }
          : { text: `Left the folder ${e.sourcePath.split('/').join(' › ')} in place`, problem: e.errorMessage, kind: 'removed' };
      }
      if (renamed && moved) {
        return { text: `Put ${q(e.originalName)} back in ${folderText(e.destinationPath)} with its old name "${e.newName}"`, problem, kind: 'putBack' };
      }
      if (renamed) {
        return { text: `Changed ${q(e.originalName)} back to its old name "${e.newName}"`, problem, kind: 'putBack' };
      }
      return { text: `Moved ${q(e.newName || nameOf(e.destinationPath))} back to ${folderText(e.destinationPath)}`, problem, kind: 'putBack' };
    case 'tag':
      return { text: `Added tags to "${e.originalName || nameOf(e.sourcePath)}"`, problem, kind: 'tagged' };
    default:
      return { text: `${e.action}: ${e.sourcePath} → ${e.destinationPath}`, problem, kind: 'other' };
  }
}

/** A one-line title for a batch of steps. */
export function batchTitle(entries: IOperationLogEntry[], isUndo: boolean): string {
  const steps: IStep[] = entries.filter(e => e.status === 'success' && e.action !== 'createFolder').map(describeStep);
  if (isUndo) {
    return `Undo: put back ${steps.filter(s => s.kind === 'putBack').length} item${steps.filter(s => s.kind === 'putBack').length === 1 ? '' : 's'}`;
  }
  const count = (k: IStep['kind']): number => steps.filter(s => s.kind === k).length;
  const parts: string[] = [];
  const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;
  if (count('filed')) { parts.push(`named and filed ${plural(count('filed'), 'document', 'documents')}`); }
  if (count('moved')) { parts.push(`moved ${plural(count('moved'), 'item', 'items')}`); }
  if (count('renamed')) { parts.push(`renamed ${plural(count('renamed'), 'file', 'files')}`); }
  if (count('setAside')) { parts.push(`set aside ${plural(count('setAside'), 'item', 'items')}`); }
  if (count('tagged')) { parts.push(`tagged ${plural(count('tagged'), 'file', 'files')}`); }
  const viewed: IStep[] = steps.filter(s => s.kind === 'viewed');
  if (viewed.length && viewed.length === steps.length) {
    return viewed[0].text;
  }
  const recorded: IStep[] = steps.filter(s => s.kind === 'recorded');
  if (recorded.length === 1 && !parts.length) {
    return recorded[0].text; // One change to a record: its own sentence says it best.
  }
  if (recorded.length) { parts.push(`updated ${plural(recorded.length, 'record', 'records')}`); }
  const text: string = parts.join(', ') || 'No changes';
  return text.charAt(0).toUpperCase() + text.slice(1);
}
